/**
 * API ドメイン設定
 *
 * QRコードのURLパラメータ(env)から環境を識別し、
 * 対応するAPIドメインを返す。
 *
 * QRコード形式:
 * https://liff.line.me/{LIFF_ID}?token={invitation_token}&env={environment}
 *
 * 例:
 * - dev環境: ?token=xxx&env=dev
 * - stg環境: ?token=xxx&env=stg
 * - 本番(fl001): ?token=xxx&env=fl001
 * - 本番(fl002): ?token=xxx&env=fl002
 */

/**
 * 環境識別子とAPIドメインのマッピング
 */
export const API_DOMAINS = {
  dev: 'https://dev.api.flaro.jp/external',
  stg: 'https://stg.api.flaro.jp/external',
  fl001: 'https://api.flaro.jp/external',
  fl002: 'https://fl002.api.flaro.jp/external',
} as const;

/**
 * FLARO WebアプリのベースドメインテンプレートFLARO_APP_DOMAIN_TEMPLATES
 *
 * dev環境: company_code不要（固定URL）
 * その他: {company_code}.{env}.flaro.jp の形式
 */
export const FLARO_APP_DOMAIN_TEMPLATES = {
  dev: 'https://dev.flaro.jp',
  stg: 'https://{company_code}.stg.flaro.jp',
  fl001: 'https://{company_code}.fl001.flaro.jp',
  fl002: 'https://{company_code}.fl002.flaro.jp',
} as const;

/**
 * 有効な環境識別子の型
 */
export type ApiEnvironment = keyof typeof API_DOMAINS;

const ENV_SESSION_KEY = 'flaro_liff_env';

let _cachedEnv: ApiEnvironment | null = null;
let _cachedDomain: string | null = null;

/**
 * 環境識別子が有効かどうかを検証
 */
export const isValidEnvironment = (env: string): env is ApiEnvironment => {
  return env in API_DOMAINS;
};

/**
 * liff.state パラメータを解析してURLSearchParamsを返す
 * OAuthリダイレクト後にLIFF SDKがliff.stateにエンコードするパラメータの解析用
 */
export const parseLiffStateParams = (liffState: string): URLSearchParams | null => {
  try {
    const decoded = decodeURIComponent(liffState);
    const queryPart = decoded.includes('?') ? decoded.split('?')[1] : decoded;
    return new URLSearchParams(queryPart);
  } catch (e) {
    console.warn('[LIFF] liff.stateパラメータの解析に失敗しました', e);
    return null;
  }
};

/**
 * URLパラメータから環境識別子を取得
 *
 * 取得優先順位:
 * 1. モジュールレベルキャッシュ（最速）
 * 2. 直接URLパラメータ（?env=fl001）
 * 3. liff.state エンコード済みパラメータ（OAuthリダイレクト後）
 * 4. sessionStorage（同一LIFFセッション内のページ遷移に対応）
 */
export const getEnvironmentFromUrl = (): ApiEnvironment | null => {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);

  // 1. 直接URLパラメータから取得（キャッシュより優先）
  const env = params.get('env');
  if (env && isValidEnvironment(env)) {
    _cachedEnv = env;
    persistEnvironment(env);
    return env;
  }

  // キャッシュがあればそれを返す（URLにenvがない場合）
  if (_cachedEnv) return _cachedEnv;

  // 2. liff.state から取得
  const liffState = params.get('liff.state');
  if (liffState) {
    const stateParams = parseLiffStateParams(liffState);
    if (stateParams) {
      const envFromState = stateParams.get('env');
      if (envFromState && isValidEnvironment(envFromState)) {
        _cachedEnv = envFromState;
        persistEnvironment(envFromState);
        return envFromState;
      }
    }
  }

  // 3. sessionStorage から取得（リダイレクト前に保存された値）
  try {
    const stored = sessionStorage.getItem(ENV_SESSION_KEY);
    if (stored && isValidEnvironment(stored)) {
      _cachedEnv = stored;
      return stored;
    }
  } catch {
    // sessionStorage アクセスエラーは無視
  }

  return null;
};

/**
 * 環境識別子をsessionStorageに永続化
 * OAuthリダイレクト後にも環境情報を維持するため
 */
export const persistEnvironment = (env: ApiEnvironment): void => {
  _cachedEnv = env;
  _cachedDomain = null; // 環境変更時にドメインキャッシュをリセット
  try {
    sessionStorage.setItem(ENV_SESSION_KEY, env);
  } catch {
    // sessionStorage が利用できない環境では無視
  }
};

/**
 * 環境キャッシュをクリア（テスト用）
 */
export const clearEnvironmentCache = (): void => {
  _cachedEnv = null;
  _cachedDomain = null;
  try {
    sessionStorage.removeItem(ENV_SESSION_KEY);
  } catch {
    // ignore
  }
};

/**
 * APIドメインを取得
 *
 * 優先順位:
 * 1. URLパラメータの env から取得
 * 2. 環境変数 VITE_API_BASE_URL から取得
 * 3. デフォルト (dev環境)
 */
export const getApiDomain = (): string => {
  if (_cachedDomain) return _cachedDomain;

  // 1. URLパラメータからの取得を試みる
  const envFromUrl = getEnvironmentFromUrl();
  if (envFromUrl) {
    console.info(`[API Domain] Using environment from URL: ${envFromUrl}`);
    _cachedDomain = API_DOMAINS[envFromUrl];
    return _cachedDomain;
  }

  // 2. 環境変数から取得
  const envVar = import.meta.env.VITE_API_BASE_URL;
  if (envVar) {
    console.info('[API Domain] Using environment variable');
    _cachedDomain = envVar;
    return _cachedDomain;
  }

  // 3. デフォルト
  console.warn('[API Domain] No configuration found, using default (dev)');
  _cachedDomain = API_DOMAINS.dev;
  return _cachedDomain;
};

/**
 * 現在の環境識別子を取得
 */
export const getCurrentEnvironment = (): ApiEnvironment | 'custom' => {
  const envFromUrl = getEnvironmentFromUrl();
  if (envFromUrl) {
    return envFromUrl;
  }

  // 環境変数のドメインから環境を逆引き
  const envVar = import.meta.env.VITE_API_BASE_URL;
  if (envVar) {
    const entry = Object.entries(API_DOMAINS).find(
      ([, domain]) => domain === envVar
    );
    if (entry) {
      return entry[0] as ApiEnvironment;
    }
  }

  return 'custom';
};

/**
 * FLARO WebアプリのURLを取得
 *
 * dev環境: https://dev.flaro.jp（company_code不要）
 * その他: https://{company_code}.{env}.flaro.jp
 *
 * @param env 環境識別子（dev, stg, fl001, fl002）
 * @param companyCode 会社コード（dev以外の環境で必須）
 */
export const getFlaroAppUrl = (env?: string | null, companyCode?: string): string => {
  // 環境を決定
  let targetEnv: ApiEnvironment = 'dev';

  if (env && isValidEnvironment(env)) {
    targetEnv = env;
  } else {
    const envFromUrl = getEnvironmentFromUrl();
    if (envFromUrl) {
      targetEnv = envFromUrl;
    } else {
      // URLパラメータがない場合、VITE_API_BASE_URLから環境を逆引き
      const envVar = import.meta.env.VITE_API_BASE_URL;
      if (envVar) {
        const entry = Object.entries(API_DOMAINS).find(([, domain]) => domain === envVar);
        if (entry) {
          targetEnv = entry[0] as ApiEnvironment;
        }
      }
    }
  }

  // テンプレートを取得
  const template = FLARO_APP_DOMAIN_TEMPLATES[targetEnv];

  // dev環境はそのまま返す（company_code不要）
  if (targetEnv === 'dev') {
    console.info('[FLARO App URL] Using dev environment');
    return template;
  }

  // dev以外はcompany_codeが必須
  if (!companyCode) {
    console.warn('[FLARO App URL] company_code is required for non-dev environment, falling back to dev');
    return FLARO_APP_DOMAIN_TEMPLATES.dev;
  }

  // テンプレートのプレースホルダを置換
  const url = template.replace('{company_code}', companyCode.toLowerCase());
  console.info(`[FLARO App URL] Using ${targetEnv} environment with company_code: ${companyCode}`);
  return url;
};
